'use client';

import { App } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { UpdateAssetLogRequest } from '@evimo/types/api/goldenRecord/update-asset-log/update-asset-log.request.dto';
import { AssetLogsService } from '~/app/services/goldenRecord/asset-logs.service';

type UpdateAssetLogVariables = {
  logId: string;
  payload: UpdateAssetLogRequest;
};

export type UseUpdateAssetLogResult = {
  isPending: boolean;
  updateLog: (variables: UpdateAssetLogVariables) => Promise<void>;
};

export function useUpdateAssetLog(
  organizationId: string,
  assetId: string,
): UseUpdateAssetLogResult {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { t } = useTranslation('assets');

  const mutation = useMutation({
    mutationFn: ({ logId, payload }: UpdateAssetLogVariables) =>
      AssetLogsService.update(organizationId, assetId, logId, payload),
    onSuccess: async () => {
      message.success(t('rawLogModal.saveSuccess'));
      await queryClient.invalidateQueries({
        queryKey: ['asset-logs', organizationId, assetId],
      });
    },
    onError: (error) => {
      message.error(
        error instanceof Error ? error.message : t('rawLogModal.saveError'),
      );
    },
  });

  return {
    isPending: mutation.isPending,
    updateLog: mutation.mutateAsync,
  };
}
